import { useState, useEffect } from 'react';

/**
 * useDebounce Hook
 *
 * A custom hook to debounce a value.
 *
 * @param value - The value to be debounced.
 * @param delay - The delay in milliseconds (default: 300ms).
 * @returns The debounced value.
 *
 * Usage:
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set a timeout to update the debounced value after the delay
    const handler = globalThis.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup the timeout if the value or delay changes
    // (This is how debouncing works: it cancels the previous timer)
    return () => {
      globalThis.clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
